import React from 'react';
import PropTypes from 'prop-types';

import {Box, Stack, Typography, Paper, Divider, Button, TextField, IconButton} from 'views/components';
import {DeleteForever, Add, CloudUpload} from 'views/icons';
import {Translate, translate, DatePicker, UploadFileToS3, SecureFileGrid} from 'views/module';

import moment from 'moment';

const EducationEdit = (props) => {
  const {bioQuestions, setFields, setIsComplete, getSignedFileUrl} = props;

  const createDefaultEducationEntry = () => ({
    id: `education-${Date.now()}-${Math.floor(Math.random() * 1000)}`,
    school: '',
    fieldOfStudy: '',
    degree: '',
    dateObtained: '',
    documents: [],
  });

  const [education, setEducation] = React.useState([createDefaultEducationEntry()]);

  React.useEffect(() => {
    const sourceEducation = bioQuestions?.education?.entries || [];

    setEducation(sourceEducation.length ? sourceEducation : [createDefaultEducationEntry()]);
  }, [bioQuestions]);

  React.useEffect(() => {
    const filteredEducation = education.filter((entry) => (
      typeof entry?.school === 'string' && entry.school.trim()
    ));

    setFields((prev) => ({
      ...prev,
      education: {
        entries: filteredEducation,
      },
    }));
  }, [education, setFields]);

  React.useEffect(() => {
    setIsComplete(education.length > 0);
  }, [education, setIsComplete]);

  const handleChange = (index, field) => (value) => {
    setEducation((prev) => {
      return prev.map((entry, i) => {
        if (i !== index) return entry;

        return {
          ...entry,
          [field]: value,
        };
      });
    });
  };

  const handleAdd = () => {
    setEducation((prev) => [...prev, createDefaultEducationEntry()]);
  };

  const handleDelete = (index) => {
    setEducation((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRemoveDocument = (index, fileToRemove) => {
    setEducation((prev) => {
      return prev.map((entry, i) => {
        if (i !== index) return entry;

        return {
          ...entry,
          documents: (entry.documents || []).filter((file) => file.src !== fileToRemove.src),
        };
      });
    });
  };

  const getDocumentSignedUrl = (index) => async (file) => {
    const onComplete = (fileUrl) => {
      setEducation((prev) => {
        return prev.map((entry, i) => {
          if (i !== index) return entry;

          return {
            ...entry,
            documents: [...(entry.documents || []), {name: file.name, type: file.type, src: fileUrl}],
          };
        });
      });
    };

    return getSignedFileUrl('education', file, false, onComplete);
  };

  return (
    <Stack spacing={3}>
      <Typography variant="h6" textAlign="center">
        <Translate expression="caregiver.CaregiverBiography.Education.Title" />
      </Typography>

      <Stack spacing={2.5}>
        {
          education.map((entry, index) => (
            <Paper key={`education-${index}`} sx={{p: 2.5, borderRadius: 2}}>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                    {
                      entry.school?.trim() ||
                      translate('caregiver.CaregiverBiography.Education.EntryLabel', {number: index + 1})
                    }
                  </Typography>
                  <IconButton color="error" onClick={() => handleDelete(index)}>
                    <DeleteForever fontSize="small" />
                  </IconButton>
                </Stack>

                <Divider />

                <TextField
                  label={translate('caregiver.CaregiverBiography.Education.School.Label')}
                  placeholder={translate('caregiver.CaregiverBiography.Education.School.Placeholder')}
                  value={entry.school}
                  onChange={handleChange(index, 'school')}
                  fullWidth
                />

                <TextField
                  label={translate('caregiver.CaregiverBiography.Education.FieldOfStudy.Label')}
                  placeholder={translate('caregiver.CaregiverBiography.Education.FieldOfStudy.Placeholder')}
                  value={entry.fieldOfStudy}
                  onChange={handleChange(index, 'fieldOfStudy')}
                  fullWidth
                />

                <Stack direction={{xs: 'column', sm: 'row'}} spacing={2}>
                  <TextField
                    label={translate('caregiver.CaregiverBiography.Education.Degree.Label')}
                    placeholder={translate('caregiver.CaregiverBiography.Education.Degree.Placeholder')}
                    value={entry.degree}
                    onChange={handleChange(index, 'degree')}
                    fullWidth
                  />
                  <DatePicker
                    label={translate('caregiver.CaregiverBiography.Education.DateObtained.Label')}
                    format="DD MMM YYYY"
                    value={entry.dateObtained ? moment(entry.dateObtained) : null}
                    onChange={(date) => handleChange(index, 'dateObtained')(
                      date ? moment(date).startOf('day').toISOString() : '',
                    )}
                    textFieldProps={{
                      fullWidth: true,
                      placeholder: translate('caregiver.CaregiverBiography.Education.DateObtained.Placeholder'),
                    }}
                  />
                </Stack>

                <Stack spacing={1.5}>
                  <Stack spacing={0.5}>
                    <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                      <Translate expression="caregiver.CaregiverBiography.Education.Documents" />
                    </Typography>
                    {
                      entry.documents?.length ?
                        <SecureFileGrid
                          files={entry.documents}
                          menuItems={[
                            {
                              Icon: DeleteForever,
                              text: translate('generic.Delete'),
                              handler: (file) => handleRemoveDocument(index, file),
                            },
                          ]}
                        /> :
                        <Typography variant="body2" color="text.secondary">
                          <Translate expression="caregiver.CaregiverBiography.Education.EmptyState" />
                        </Typography>
                    }
                  </Stack>

                  <UploadFileToS3 getSignedUrl={getDocumentSignedUrl(index)}>
                    <Box
                      sx={(theme) => ({
                        width: '100%',
                        minHeight: 140,
                        borderRadius: '16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        padding: theme.spacing(2),
                        border: `2px dashed ${
                          theme.palette.mode === 'light' ?
                            'rgba(0, 0, 0, 0.12)' :
                            'rgba(255, 255, 255, 0.25)'
                        }`,
                      })}
                    >
                      <Stack spacing={0.75} alignItems="center" textAlign="center">
                        <CloudUpload fontSize="medium" />
                        <Typography variant="subtitle2" color="text.primary" sx={{fontWeight: 600}}>
                          <Translate expression="caregiver.CaregiverBiography.Education.UploadTitle" />
                        </Typography>
                      </Stack>
                    </Box>
                  </UploadFileToS3>
                </Stack>
              </Stack>
            </Paper>
          ))
        }
      </Stack>

      <Button
        variant="outlined"
        color="primary"
        onClick={handleAdd}
        startIcon={<Add />}
        sx={{alignSelf: 'center', borderStyle: 'dashed', borderWidth: 1, px: 3, py: 1.5}}
      >
        <Translate expression="caregiver.CaregiverBiography.Education.AddEducationButton" />
      </Button>

      {
        education.length > 0 && (
          <Paper sx={{p: 2.5, borderRadius: 2}}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                <Translate
                  expression="caregiver.CaregiverBiography.Education.Summary.Title"
                  substitute={{
                    count: education.length,
                    entries:
                      education.length === 1 ?
                        translate('caregiver.CaregiverBiography.Education.Summary.EntrySingular') :
                        translate('caregiver.CaregiverBiography.Education.Summary.EntryPlural'),
                  }}
                />
              </Typography>
              <Box display="flex" flexDirection="column" gap={0.75}>
                {
                  education.map((entry, index) => (
                    <Typography key={`summary-${index}`} variant="body2">
                      <Typography component="span" variant="body2" sx={{fontWeight: 600}}>
                        {
                          entry.school ||
                          translate('caregiver.CaregiverBiography.Education.Summary.UnnamedEntry')
                        }
                      </Typography>
                      {entry.fieldOfStudy && ` • ${entry.fieldOfStudy}`}
                      {entry.degree && ` • ${entry.degree}`}
                      {
                        entry.dateObtained && moment(entry.dateObtained).isValid() &&
                        ` • ${moment(entry.dateObtained).format('YYYY')}`
                      }
                    </Typography>
                  ))
                }
              </Box>
            </Stack>
          </Paper>
        )
      }
    </Stack>
  );
};

EducationEdit.propTypes = {
  bioQuestions: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  setFields: PropTypes.func.isRequired,
  setIsComplete: PropTypes.func,
  getSignedFileUrl: PropTypes.func,
};

export default React.memo(EducationEdit);

